import { haptic } from '../../lib/native';
import { zodResolver } from '@hookform/resolvers/zod';
import { displayName, LINK_KINDS, LINK_LABEL, parseEmail, parseLink, detectDateOrder, parsePhone, readDate, type Graph, type Person } from '@lineage/core';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Modal } from '../../components/Modal';
import { api, ApiError } from '../../lib/api';
import { withCode } from '../../lib/errorCode';
import type { EncodedPhoto } from '../../lib/photo';
import { keys } from '../../lib/queries';
import { DateField } from './DateField';
import { PhotoPicker } from './PhotoPicker';
import { translateMessage, useT } from '../../i18n';

const Schema = z.object({
  firstName: z.string().trim().max(80),
  lastName: z.string().trim().max(80),
  birthName: z.string().trim().max(80),
  gender: z.enum(['', 'female', 'male', 'other']),
  born: z.string().trim().max(120),
  birthPlace: z.string().trim().max(120),
  living: z.boolean(),
  died: z.string().trim().max(120),
  restingPlace: z.string().trim().max(120),
  occupation: z.string().trim().max(120),
  residence: z.string().trim().max(120),
  story: z.string().max(20000),
  email: z.string().max(254),
  phone: z.string().max(40),
  instagram: z.string().max(300),
  facebook: z.string().max(300),
  linkedin: z.string().max(300),
  twitter: z.string().max(300),
  website: z.string().max(300),
  isStepParent: z.boolean(),
  otherParentId: z.string(),
  linkAsPartnerOfExistingParent: z.boolean(),
  parent1: z.string(),
  parent2: z.string(),
}).refine((v) => v.firstName.trim() || v.lastName.trim(), { message: 'Enter a first or last name', path: ['firstName'] }).superRefine((v, ctx) => {
  for (const k of ['born', 'died'] as const) {
    if (/^\d{1,3}$/.test(v[k].trim())) ctx.addIssue({ code: 'custom', message: 'Enter a four-digit year', path: [k] });
  }
  const ph = parsePhone(v.phone);
  if (ph.error !== undefined) ctx.addIssue({ code: 'custom', message: ph.error, path: ['phone'] });
  const e = parseEmail(v.email);
  if (e.error !== undefined) ctx.addIssue({ code: 'custom', message: e.error, path: ['email'] });
  for (const k of LINK_KINDS) {
    const r = parseLink(k, v[k]);
    if (r.error !== undefined) ctx.addIssue({ code: 'custom', message: r.error, path: [k] });
  }
});
type Values = z.infer<typeof Schema>;

export type RelationType = 'parent' | 'partner' | 'child' | 'sibling';
export type FormTarget =
  | { mode: 'create'; anchorId: string | null; type: RelationType | null; thisIsMe?: boolean; joinAsNew?: boolean; chooseWho?: boolean }
  | { mode: 'edit'; person: Person };

const RELATIONS: RelationType[] = ['parent', 'partner', 'child', 'sibling'];

interface Props {
  treeId: string;
  graph: Graph;
  target: FormTarget;
  onClose: () => void;
  onSaved: (personId: string) => void;
  /** Link the new person to the signed-in account (Add yourself). */
  onCreatedAsMe?: (personId: string) => Promise<void>;
  /** Whether the current user can still add themselves (only with target.chooseWho). */
  canAddSelf?: boolean;
  /** Whether this person may change the card's parents (its creator or an owner). */
  canChangeLinks?: boolean;
}

async function upload(treeId: string, p: EncodedPhoto): Promise<string> {
  const form = new FormData();
  form.set('full', new File([p.full], 'photo.jpg', { type: 'image/jpeg' }));
  form.set('thumb', new File([p.thumb], 'photo_256.jpg', { type: 'image/jpeg' }));
  form.set('width', '1024');
  form.set('height', '1024');
  return (await api<{ id: string }>('POST', `/trees/${treeId}/photos`, form)).id;
}

export function PersonForm({ treeId, graph, target, onClose, onSaved, onCreatedAsMe, canAddSelf, canChangeLinks = true }: Props) {
  const t = useT();
  const tf = t.form;
  const rel = t.common.relation;
  /** Validation messages are written in English; show them in the screen language. */
  const msg = (m?: string) => (m ? translateMessage(m) : '');
  const qc = useQueryClient();
  const editing = target.mode === 'edit' ? target.person : null;
  const [relType, setRelType] = useState<RelationType | null>(target.mode === 'create' ? target.type : null);
  const chooseWho = target.mode === 'create' && Boolean(target.chooseWho);
  const [forMe, setForMe] = useState(false);
  const joinAsNew = target.mode === 'create' && (Boolean(target.joinAsNew) || (chooseWho && forMe));
  const [anchorSel, setAnchorSel] = useState<string>(target.mode === 'create' ? target.anchorId ?? '' : '');
  const anchorId = target.mode === 'create' ? anchorSel || null : null;
  const anchor = anchorId ? graph.people.get(anchorId) : undefined;
  const [photo, setPhoto] = useState<EncodedPhoto | null>(null);
  const [removeExisting, setRemoveExisting] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const parentsOfEdit = editing ? graph.parents.get(editing.id) ?? [] : [];
  const { register, handleSubmit, watch, setValue, formState } = useForm<Values>({
    resolver: zodResolver(Schema),
    defaultValues: {
      firstName: editing?.firstName ?? '',
      lastName: editing?.lastName ?? '',
      birthName: editing?.birthName ?? '',
      gender: editing?.gender ?? '',
      born: editing?.born ?? '',
      birthPlace: editing?.birthPlace ?? '',
      living: editing?.living ?? true,
      died: editing?.died ?? '',
      restingPlace: editing?.restingPlace ?? '',
      occupation: editing?.occupation ?? '',
      residence: editing?.residence ?? '',
      story: editing?.story ?? '',
      email: editing?.email ?? '',
      phone: editing?.phone ?? '',
      instagram: editing?.instagram ?? '',
      facebook: editing?.facebook ?? '',
      linkedin: editing?.linkedin ?? '',
      twitter: editing?.twitter ?? '',
      website: editing?.website ?? '',
      isStepParent: false,
      otherParentId: anchor && relType === 'child' ? (graph.partners.get(anchor.id) ?? []).length === 1 ? graph.partners.get(anchor.id)![0]! : '' : '',
      linkAsPartnerOfExistingParent: true,
      parent1: parentsOfEdit[0] ?? '',
      parent2: parentsOfEdit[1] ?? '',
    },
  });
  const living = watch('living');
  const first = watch('firstName');
  const last = watch('lastName');
  const born = watch('born');
  const died = watch('died');
  // Older entries like 24/04/1971 are read the way this tree's other dates are written.
  const dateOrder = useMemo(() => detectDateOrder([...graph.people.values()].map((p) => p.born)), [graph]);
  const bornRead = readDate(born, dateOrder);
  const bornIso = bornRead.kind === 'full' ? bornRead.iso : null;
  /** What gets saved: dates we understood are stored in the picker's format, anything else is kept as typed. */
  const tidyDate = (text: string) => {
    const r = readDate(text, dateOrder);
    return r.kind === 'full' ? r.iso : text;
  };

  const anchorKids = anchor ? graph.children.get(anchor.id) ?? [] : [];
  const singleParentKids = anchorKids.filter((k) => (graph.parents.get(k) ?? []).length === 1);
  const anchorPartners = anchor ? graph.partners.get(anchor.id) ?? [] : [];
  const anchorParents = anchor ? graph.parents.get(anchor.id) ?? [] : [];
  const others = [...graph.people.values()].filter((p) => p.id !== editing?.id);

  // A throwaway person for the live portrait preview.
  const preview: Person = {
    ...(editing ?? {
      id: 'new', gender: null, birthName: null, born: null, bornYear: null, birthPlace: null, died: null, diedYear: null,
      restingPlace: null, occupation: null, residence: null, story: null, photoId: null,
      email: null, phone: null, instagram: null, facebook: null, linkedin: null, twitter: null, website: null, createdAt: 0, living: true,
    }),
    firstName: first || null,
    lastName: last || null,
    living,
    photoId: removeExisting ? null : editing?.photoId ?? null,
  };

  const submit = handleSubmit(async (v) => {
    setError('');
    setBusy(true);
    try {
      const photoId = photo ? await upload(treeId, photo) : undefined;
      const fields = {
        firstName: v.firstName || null,
        lastName: v.lastName || null,
        birthName: v.birthName || null,
        gender: v.gender || null,
        born: tidyDate(v.born) || null,
        birthPlace: v.birthPlace || null,
        living: v.living,
        died: v.living ? null : tidyDate(v.died) || null,
        restingPlace: v.restingPlace || null,
        occupation: v.occupation || null,
        residence: v.residence || null,
        story: v.story || null,
        // the server normalises these (adds https://, turns @name into a link) and rejects anything unsafe
        email: v.email || null,
        phone: v.phone || null,
        instagram: v.instagram || null,
        facebook: v.facebook || null,
        linkedin: v.linkedin || null,
        twitter: v.twitter || null,
        website: v.website || null,
      };
      let id: string;
      if (editing) {
        await api('PATCH', `/trees/${treeId}/people/${editing.id}`, { ...fields, ...(photoId ? { photoId } : removeExisting ? { photoId: null } : {}) });
        const next = [v.parent1, v.parent2].filter(Boolean);
        if (canChangeLinks && next.join() !== parentsOfEdit.join()) await api('PUT', `/trees/${treeId}/people/${editing.id}/parents`, { parentIds: next });
        id = editing.id;
      } else {
        const relation = anchor && relType
          ? {
              anchorId: anchor.id,
              type: relType,
              isStepParent: relType === 'partner' ? v.isStepParent : false,
              otherParentId: relType === 'child' && v.otherParentId ? v.otherParentId : null,
              linkAsPartnerOfExistingParent: relType === 'parent' && v.linkAsPartnerOfExistingParent,
            }
          : null;
        const r = joinAsNew
          ? await api<{ person: string }>('POST', `/trees/${treeId}/self`, { person: fields, relation })
          : await api<{ person: string }>('POST', `/trees/${treeId}/people`, { person: fields, relation, photoId });
        id = r.person;
        if (target.mode === 'create' && (target.thisIsMe || (chooseWho && forMe))) await onCreatedAsMe?.(id);
      }
      await qc.invalidateQueries({ queryKey: keys.tree(treeId) });
      haptic();
      onSaved(id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : withCode(tf.couldntSave, e));
    } finally {
      setBusy(false);
    }
  });

  const title = chooseWho ? tf.addSomeone : joinAsNew ? tf.addYourselfToTree : editing ? tf.edit(displayName(editing)) : anchor ? tf.addRelOf(relType ? rel[relType].toLowerCase() : tf.relative, displayName(anchor)) : target.mode === 'create' && target.thisIsMe ? tf.addYourself : tf.addPerson;
  const showStep = !editing && relType === 'partner' && singleParentKids.length > 0;

  return (
    <Modal title={title} onClose={onClose} full>
      <form className="stack" onSubmit={submit} noValidate>
        {chooseWho && canAddSelf && (
          <div className="field">
            <span>{tf.whoAdding}</span>
            <div className="segmented" role="group" aria-label={tf.whoAdding}>
              <button type="button" aria-pressed={forMe} onClick={() => { setForMe(true); setRelType(null); }}>{tf.myself}</button>
              <button type="button" aria-pressed={!forMe} onClick={() => { setForMe(false); setRelType(null); }}>{tf.someoneElse}</button>
            </div>
          </div>
        )}
        {(joinAsNew || chooseWho) && (
          <label className="field">
            <span>{joinAsNew ? tf.closestTo : tf.relatedTo}</span>
            <select className="input" value={anchorSel} onChange={(e) => { setAnchorSel(e.target.value); setRelType(null); }}>
              <option value="">{joinAsNew ? tf.chooseRelative : tf.noOneYet}</option>
              {others.map((p) => <option key={p.id} value={p.id}>{displayName(p)}</option>)}
            </select>
          </label>
        )}
        {!editing && anchor && (
          <div className="field">
            <span>{joinAsNew ? tf.youAre(displayName(anchor)) : chooseWho ? tf.theyAre(displayName(anchor)) : tf.relationshipTo(displayName(anchor))}</span>
            <div className="segmented" role="group" aria-label={tf.relationship}>
              {RELATIONS.map((r) => (
                <button key={r} type="button" aria-pressed={relType === r} onClick={() => setRelType(r)}>{rel[r]}</button>
              ))}
            </div>
          </div>
        )}
        {showStep && (
          <div className="banner">
            {tf.shownAsParent(singleParentKids.map((k) => displayName(graph.people.get(k)!)).join(t.profile.and))}
            <label className="row" style={{ marginTop: 8 }}>
              <input type="checkbox" {...register('isStepParent')} /> {tf.stepParent}
            </label>
          </div>
        )}
        {!editing && relType === 'child' && anchorPartners.length > 0 && (
          <label className="field">
            <span>{tf.otherParent}</span>
            <select className="input" {...register('otherParentId')}>
              <option value="">{tf.notListed}</option>
              {anchorPartners.map((p) => <option key={p} value={p}>{displayName(graph.people.get(p)!)}</option>)}
            </select>
          </label>
        )}
        {!editing && relType === 'parent' && anchorParents.length === 1 && (
          <label className="row">
            <input type="checkbox" {...register('linkAsPartnerOfExistingParent')} /> {tf.partnerOf(displayName(graph.people.get(anchorParents[0]!)!))}
          </label>
        )}

        {!joinAsNew && <PhotoPicker person={preview} value={photo} onChange={setPhoto} onRemoveExisting={() => setRemoveExisting(true)} />}

        <div className="row form-row">
          <label className="field grow"><span>{tf.firstName}</span><input className="input" autoComplete="off" {...register('firstName')} /></label>
          <label className="field grow"><span>{tf.lastName}</span><input className="input" autoComplete="off" {...register('lastName')} /></label>
        </div>
        {formState.errors.firstName && <div className="error">{msg(formState.errors.firstName.message)}</div>}
        <div className="row form-row">
          <label className="field grow"><span>{tf.birthSurname}</span><input className="input" {...register('birthName')} /></label>
          <label className="field grow">
            <span>{tf.gender}</span>
            <select className="input" {...register('gender')}>
              <option value="">{tf.notSpecified}</option>
              <option value="female">{tf.female}</option>
              <option value="male">{tf.male}</option>
              <option value="other">{tf.other}</option>
            </select>
          </label>
        </div>
        <div className="row form-row">
          <div className="grow"><DateField label={tf.born} order={dateOrder} value={born} onChange={(v) => setValue('born', v, { shouldDirty: true })} />{formState.errors.born && <span className="error">{msg(formState.errors.born.message)}</span>}</div>
          <label className="field grow"><span>{tf.birthplace}</span><input className="input" {...register('birthPlace')} /></label>
        </div>
        <label className="row"><input type="checkbox" {...register('living')} /> {tf.living}</label>
        {!living && (
          <div className="row form-row">
            <DateField label={tf.died} order={dateOrder} value={died} min={bornIso ?? '1000-01-01'} onChange={(v) => setValue('died', v, { shouldDirty: true })} />
            <label className="field grow"><span>{tf.restingPlace}</span><input className="input" {...register('restingPlace')} /></label>
          </div>
        )}
        <div className="row form-row">
          <label className="field grow"><span>{tf.occupation}</span><input className="input" {...register('occupation')} /></label>
          {living && <label className="field grow"><span>{tf.livesIn}</span><input className="input" {...register('residence')} /></label>}
        </div>
        <label className="field"><span>{tf.story}</span><textarea className="input" maxLength={20000} {...register('story')} /></label>
        <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="eyebrow" style={{ marginBottom: 8 }}>{tf.online}</legend>
          <label className="field">
            <span>{tf.email}</span>
            <input className="input" type="text" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="off" placeholder="name@example.com" {...register('email')} />
            {formState.errors.email && <span className="error">{msg(formState.errors.email.message)}</span>}
          </label>
          <label className="field">
            <span>{tf.phone}</span>
            <input className="input" type="text" inputMode="tel" autoComplete="off" placeholder="+1 415 555 2671" {...register('phone')} />
            {formState.errors.phone ? <span className="error">{msg(formState.errors.phone.message)}</span> : <span className="muted" style={{ fontSize: 12 }}>{tf.phoneHint}</span>}
          </label>
          {LINK_KINDS.map((k) => (
            <label key={k} className="field">
              <span>{LINK_LABEL[k]}</span>
              <input className="input" type="text" inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="off" placeholder={tf.linkPlaceholder[k]} {...register(k)} />
              {formState.errors[k] && <span className="error">{msg(formState.errors[k]?.message)}</span>}
            </label>
          ))}
        </fieldset>

        {editing && canChangeLinks && (
          <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="eyebrow" style={{ marginBottom: 8 }}>{tf.familyLinks}</legend>
            <div className="row">
              {(['parent1', 'parent2'] as const).map((f, i) => (
                <label key={f} className="field grow">
                  <span>{tf.parentN(i + 1)}</span>
                  <select className="input" {...register(f)}>
                    <option value="">{tf.none}</option>
                    {others.map((p) => <option key={p.id} value={p.id}>{displayName(p)}</option>)}
                  </select>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {error && <div className="error" role="alert">{error}</div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn" type="button" onClick={onClose}>{t.common.cancel}</button>
          <button className="btn btn-primary" type="submit" disabled={busy || (!editing && (joinAsNew ? !anchor || !relType : Boolean(anchor) && !relType))}>{busy ? t.common.saving : t.common.save}</button>
        </div>
      </form>
    </Modal>
  );
}
